/**
 * @file Utilitaires de formatage des nombres et des montants monétaires.
 * @module packages.common.utils.formatNumber
 *
 * Les fonctions monétaires (`fCurrency`, `eCurrency`) acceptent un paramètre
 * optionnel `currency` (ISO 4217). En l'absence de ce paramètre, elles utilisent
 * la devise par défaut du module, initialisée à 'EUR' et mise à jour dynamiquement
 * par `CurrencyProvider` lors du chargement de session.
 */

import numeral from 'numeral';

/** Devise active pour toute l'application (mise à jour par CurrencyProvider). */
let _defaultCurrency = 'EUR';

/**
 * Met à jour la devise par défaut du module.
 *
 * Appelé par `CurrencyProvider` au montage et à chaque changement de company.
 * Tous les appels existants à `fCurrency`/`eCurrency` sans paramètre de devise
 * utiliseront automatiquement la nouvelle valeur.
 *
 * @param {string} currency - Code devise ISO 4217 (ex: 'EUR', 'CHF')
 */
export function _setDefaultCurrency(currency) {
  _defaultCurrency = currency || 'EUR';
}

/**
 * Formatte un nombre en un format lisible.
 * @param {number} number - Le nombre à formater.
 * @return {string} Le nombre formaté.
 */
export function fNumber(number) {
  return numeral(number).format();
}

/**
 * Convertit un montant en centimes vers un format monétaire avec symbole.
 *
 * @param {number} number - Montant en centimes.
 * @param {string} [currency] - Code devise ISO 4217 (utilise la devise par défaut si absent).
 * @return {string} Montant formaté avec symbole monétaire (ex: "12,50 €", "12,50 CHF").
 */
export function fCurrency(number, currency) {
  const curr = (currency || _defaultCurrency).toUpperCase();
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number / 100);
}

/**
 * Formate un montant déjà en unité principale (non centimes) avec symbole monétaire.
 *
 * @param {number} number - Montant en unité principale (ex: 12.50 pour 12,50 €).
 * @param {string} [currency] - Code devise ISO 4217 (utilise la devise par défaut si absent).
 * @return {string} Montant formaté avec symbole monétaire (ex: "12,50 €", "12,50 CHF").
 */
export function eCurrency(number, currency) {
  const curr = (currency || _defaultCurrency).toUpperCase();
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

/**
 * Formate un montant pour les champs de saisie (SANS symbole monétaire).
 *
 * @param {number} number - Montant en unité principale.
 * @return {string} Montant formaté sans symbole (ex: "15,50").
 */
export function fCurrencyInput(number) {
  if (typeof number !== 'number' || isNaN(number)) return '0,00';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

/**
 * Convertit un nombre en pourcentage.
 * @param {number} number - Le nombre à convertir.
 * @return {string} Le pourcentage.
 */
export function fPercent(number) {
  const format = number ? numeral(Number(number) / 100).format('0.0%') : '';
  return result(format, '.0');
}

/**
 * Convertit un grand nombre en un format abrégé.
 * @param {number} number - Le nombre à convertir.
 * @return {string} Le nombre en format abrégé.
 */
export function fShortenNumber(number) {
  const format = number ? numeral(number).format('0.00a') : '';
  return result(format, '.00');
}

/**
 * Formatte un nombre pour les données (ex: octets en KB, MB, etc.).
 * @param {number} number - Le nombre à formater.
 * @return {string} Le nombre formaté pour les données.
 */
export function fData(number) {
  const format = number ? numeral(number).format('0 b') : '';
  return format;
}

/**
 * Fonction d'assistance pour nettoyer le formatage si nécessaire.
 * @param {string} format - Le format à nettoyer.
 * @param {string} [key='.00'] - La clé à utiliser pour le nettoyage.
 * @return {string} Le format nettoyé.
 */
function result(format, key = '.00') {
  const isInteger = format.includes(key);
  return isInteger ? format.replace(key, '') : format;
}
